import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Shield, Smartphone, Copy, Check, AlertTriangle, Download, ArrowRight } from 'lucide-react';
import api from '../api';
import toast from 'react-hot-toast';
import { tokenStorage } from '../utils/tokenStorage';

interface MFASetupData {
  qr_code: string;
  secret: string;
  backup_codes: string[];
}

const MFASetup: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [initializing, setInitializing] = useState(true);
  const [mfaSetup, setMfaSetup] = useState<MFASetupData | null>(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const [step, setStep] = useState<'setup' | 'verify' | 'backup'>('setup');
  const [backupCodesDownloaded, setBackupCodesDownloaded] = useState(false);

  // Get state from navigation
  const { mfaSetupToken, userId, userEmail, enforced, mfaInstruction } = location.state || {};

  useEffect(() => {
    if (!mfaSetupToken || !userId) {
      toast.error('Invalid MFA setup session. Please login again.');
      navigate('/login');
      return;
    }

    initializeMfaSetup();
  }, [mfaSetupToken, userId]);

  const initializeMfaSetup = async () => {
    try {
      setInitializing(true);
      // Use the forced-setup endpoint with the MFA setup token
      const response = await api.post('/api/mfa/forced-setup/initialize', {}, {
        headers: {
          'Authorization': `Bearer ${mfaSetupToken}`
        }
      });

      setMfaSetup({
        qr_code: response.data.qr_code,
        secret: response.data.secret,
        backup_codes: response.data.backup_codes
      });
      setStep('setup');
    } catch (error: any) {
      console.error('MFA setup initialization error:', error);
      toast.error(error.response?.data?.error || 'Failed to initialize MFA setup');
      navigate('/login');
    } finally {
      setInitializing(false);
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedCode(text);
      toast.success('Copied to clipboard');
      setTimeout(() => setCopiedCode(null), 2000);
    } catch (error) {
      toast.error('Failed to copy');
    }
  };

  const [authTokens, setAuthTokens] = useState<{ accessToken: string; refreshToken: string } | null>(null);

  const handleVerify = async () => {
    if (verificationCode.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }

    try {
      setLoading(true);
      const response = await api.post('/api/mfa/forced-setup/complete',
        { token: verificationCode },
        {
          headers: {
            'Authorization': `Bearer ${mfaSetupToken}`
          }
        }
      );

      if (response.data.accessToken && response.data.refreshToken) {
        setAuthTokens({
          accessToken: response.data.accessToken,
          refreshToken: response.data.refreshToken
        });
      }

      toast.success('MFA enabled successfully!');
      setStep('backup');
    } catch (error: any) {
      console.error('MFA verification error:', error);
      toast.error(error.response?.data?.error || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  const downloadBackupCodes = () => {
    if (!mfaSetup?.backup_codes) return;

    const content = `JARVIS RCSA - MFA Backup Codes
================================

Generated: ${new Date().toLocaleString()}
User: ${userEmail}

IMPORTANT: Store these codes in a secure location.
Each code can only be used once.

Backup Codes:
${mfaSetup.backup_codes.map((code, i) => `${i + 1}. ${code}`).join('\n')}

================================
If you lose access to your authenticator app,
use one of these codes to log in.
`;

    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'jarvis-mfa-backup-codes.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    setBackupCodesDownloaded(true);
    toast.success('Backup codes downloaded');
  };

  const handleComplete = () => {
    if (authTokens) {
      tokenStorage.setTokens(authTokens.accessToken, authTokens.refreshToken);
      api.defaults.headers.common['Authorization'] = `Bearer ${authTokens.accessToken}`;
      toast.success('MFA setup complete!');
      window.location.href = '/app/smart-start';
    } else {
      toast.success('MFA setup complete! Please login with your credentials.');
      navigate('/login');
    }
  };

  if (initializing) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-super-dark-purple to-dusky-purple flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-2xl p-8 max-w-md w-full mx-4 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Initializing MFA setup...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-super-dark-purple to-dusky-purple flex items-center justify-center px-4 py-8">
      <div className="bg-white rounded-xl shadow-2xl p-8 max-w-lg w-full">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Shield className="w-8 h-8 text-blue-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Set Up Two-Factor Authentication</h1>
          {enforced && (
            <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-3">
              <div className="flex items-center justify-center text-amber-800">
                <AlertTriangle className="w-5 h-5 mr-2" />
                <span className="text-sm font-medium">Required for Administrator Accounts</span>
              </div>
            </div>
          )}
          <p className="text-gray-500 mt-2">
            {userEmail && <span className="font-medium">{userEmail}</span>}
          </p>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center mb-8">
          <div className={`flex items-center ${step === 'setup' ? 'text-blue-600' : 'text-green-600'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
              step === 'setup' ? 'bg-blue-100' : 'bg-green-100'
            }`}>
              {step === 'setup' ? '1' : <Check className="w-4 h-4" />}
            </div>
            <span className="ml-2 text-sm font-medium">Scan QR</span>
          </div>
          <div className={`w-12 h-0.5 mx-2 ${step !== 'setup' ? 'bg-green-400' : 'bg-gray-200'}`} />
          <div className={`flex items-center ${
            step === 'verify' ? 'text-blue-600' : step === 'backup' ? 'text-green-600' : 'text-gray-400'
          }`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
              step === 'verify' ? 'bg-blue-100' : step === 'backup' ? 'bg-green-100' : 'bg-gray-100'
            }`}>
              {step === 'backup' ? <Check className="w-4 h-4" /> : '2'}
            </div>
            <span className="ml-2 text-sm font-medium">Verify</span>
          </div>
          <div className={`w-12 h-0.5 mx-2 ${step === 'backup' ? 'bg-green-400' : 'bg-gray-200'}`} />
          <div className={`flex items-center ${step === 'backup' ? 'text-blue-600' : 'text-gray-400'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
              step === 'backup' ? 'bg-blue-100' : 'bg-gray-100'
            }`}>
              3
            </div>
            <span className="ml-2 text-sm font-medium">Backup</span>
          </div>
        </div>

        {/* Step 1: Setup - QR Code */}
        {step === 'setup' && mfaSetup && (
          <div className="space-y-6">
            {/* Tenant-specific instruction banner */}
            {mfaInstruction && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 flex items-start gap-3">
                <Smartphone className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-blue-800">{mfaInstruction}</p>
              </div>
            )}
            <div className="text-center">
              <p className="text-gray-600 mb-4">
                Scan this QR code with your authenticator app (Google Authenticator, Authy, Microsoft Authenticator, etc.)
              </p>
              <div className="bg-white p-4 rounded-lg border-2 border-gray-200 inline-block">
                <img
                  src={mfaSetup.qr_code}
                  alt="MFA QR Code"
                  className="w-48 h-48 mx-auto"
                />
              </div>
            </div>

            {/* Manual Entry Key */}
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-600 mb-2">Can't scan? Enter this key manually:</p>
              <div className="flex items-center space-x-2">
                <code className="flex-1 text-sm font-mono bg-white px-3 py-2 rounded border break-all">
                  {mfaSetup.secret}
                </code>
                <button
                  onClick={() => copyToClipboard(mfaSetup.secret)}
                  className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                  title="Copy to clipboard"
                >
                  {copiedCode === mfaSetup.secret ? (
                    <Check className="w-5 h-5 text-green-600" />
                  ) : (
                    <Copy className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>

            <button
              onClick={() => setStep('verify')}
              className="w-full flex items-center justify-center py-3 px-4 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
            >
              <Smartphone className="w-5 h-5 mr-2" />
              I've Added the Account
              <ArrowRight className="w-5 h-5 ml-2" />
            </button>
          </div>
        )}

        {/* Step 2: Verify */}
        {step === 'verify' && (
          <div className="space-y-6">
            <div className="text-center">
              <p className="text-gray-600 mb-4">
                Enter the 6-digit code from your authenticator app to verify setup
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Verification Code
              </label>
              <input
                type="text"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="w-full px-4 py-3 text-center text-2xl font-mono tracking-widest border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="000000"
                maxLength={6}
                autoFocus
              />
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => setStep('setup')}
                className="flex-1 py-3 px-4 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
              >
                Back
              </button>
              <button
                onClick={handleVerify}
                disabled={loading || verificationCode.length !== 6}
                className="flex-1 flex items-center justify-center py-3 px-4 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                ) : (
                  <>
                    Verify
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Backup Codes */}
        {step === 'backup' && mfaSetup && (
          <div className="space-y-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Check className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">MFA Enabled Successfully!</h3>
              <p className="text-gray-600 mt-2">
                Save your backup codes in a secure location. You'll need them if you lose access to your authenticator app.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex items-start">
                <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 mr-2 flex-shrink-0" />
                <p className="text-sm text-amber-800">
                  <strong>Important:</strong> Each backup code can only be used once. Store them securely.
                </p>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-4">
              <div className="grid grid-cols-2 gap-2">
                {mfaSetup.backup_codes.map((code, index) => (
                  <div
                    key={index}
                    className="bg-white px-3 py-2 rounded border font-mono text-sm text-center"
                  >
                    {code}
                  </div>
                ))}
              </div>
            </div>

            <button
              onClick={downloadBackupCodes}
              className="w-full flex items-center justify-center py-3 px-4 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
            >
              <Download className="w-5 h-5 mr-2" />
              Download Backup Codes
              {backupCodesDownloaded && <Check className="w-5 h-5 ml-2 text-green-600" />}
            </button>

            <button
              onClick={handleComplete}
              className="w-full flex items-center justify-center py-3 px-4 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors"
            >
              <Shield className="w-5 h-5 mr-2" />
              Complete Setup & Login
            </button>
          </div>
        )}

        {/* Help Text */}
        <div className="mt-6 text-center">
          <p className="text-xs text-gray-500">
            Need help? Contact your system administrator.
          </p>
        </div>
      </div>
    </div>
  );
};

export default MFASetup;
